/**
 * Firestore 読み取り結果を保持する TTL 付きメモリキャッシュ。
 *
 * - get(key)              : TTL 内ならキャッシュ値を返す。切れていれば null
 * - set(key, data, ttlMs) : キャッシュに格納する
 * - invalidate(key)       : 特定キーを即時無効化（書き込み後に呼ぶ）
 * - invalidatePrefix(pfx) : プレフィックスが一致するキーを一括無効化
 * - clear()               : 全キャッシュを削除
 */

type CacheEntry<T> = {
  data: T;
  timestamp: number;
  ttlMs: number;
};

class FirestoreCache {
  private cache = new Map<string, CacheEntry<unknown>>();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.ttlMs) {
      this.cache.delete(key);
      return null;
    }
    return entry.data as T;
  }

  set<T>(key: string, data: T, ttlMs: number): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttlMs });
  }

  invalidate(key: string): void {
    this.cache.delete(key);
    console.log(`[FirestoreCache] invalidated: ${key}`);
  }

  invalidatePrefix(prefix: string): void {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        count++;
      }
    }
    if (count > 0) {
      console.log(`[FirestoreCache] invalidated ${count} entries with prefix: ${prefix}`);
    }
  }

  clear(): void {
    this.cache.clear();
    console.log('[FirestoreCache] cleared all entries');
  }
}

export const firestoreCache = new FirestoreCache();

// キャッシュキー定数
export const CACHE_KEYS = {
  /** enrollments コレクション全件集計（useEnrollmentCounts） */
  ENROLLMENT_COUNTS: 'enrollmentCounts',
  /** 特定コースの履修者数 */
  courseEnrollmentCount: (courseId: string) => `enrollmentCount:${courseId}`,
  /** 特定コースのレビュー一覧 */
  courseReviews: (courseId: string, year?: string) => `courseReviews:${courseId}:${year ?? 'all'}`,
  courseSurveyResponses: (courseId: string, year?: string) => `courseSurveyResponses:${courseId}:${year ?? 'all'}`,
  /** レビュータグマスタ */
  REVIEW_TAGS: 'reviewTags',
  /** 特定コースの昨年の履修者数 */
  lastYearEnrollment: (courseId: string) => `lastYearEnrollment:${courseId}`,
  /** レビューゲートのチェック結果 */
  reviewGate: (userId: string, academicYear: string, semester: string) =>
    `reviewGate:${userId}:${academicYear}:${semester}`,
  /** 成績ゲートのチェック結果 */
  gradeGate: (userId: string, academicYear: string, semester: string) =>
    `gradeGate:${userId}:${academicYear}:${semester}`,
};

// TTL 定数（ミリ秒）
export const CACHE_TTL = {
  ENROLLMENT_COUNTS: 5 * 60 * 1000,      // 5分
  COURSE_ENROLLMENT_COUNT: 5 * 60 * 1000, // 5分
  COURSE_REVIEWS: 15 * 60 * 1000,         // 15分 Spark read budget
  COURSE_SURVEY_RESPONSES: 15 * 60 * 1000,
  REVIEW_GATE: 3 * 60 * 1000,             // 3分
  REVIEW_TAGS: 30 * 60 * 1000,            // 30分
  LAST_YEAR_ENROLLMENT: 10 * 60 * 1000,    // 10分
  GRADE_GATE: 3 * 60 * 1000,              // 3分
};
